<script setup>
import Heading from './Heading.vue'

/**
 * Utility-page hero header. Encodes the canonical "surface-dim + soft orange
 * blur orb + eyebrowed Heading + lead paragraph" recipe that's repeated across
 * FAQ, Galerie, and the Legal pages.
 *
 * Two visual tones:
 *   - `standard` - orb at opacity-30 with bg-orange-500/20 (FAQ, Gallery, hero-y pages)
 *   - `quiet`    - orb at opacity-20 with bg-orange-500/10 (Legal docs - more sober)
 *
 * Two container widths:
 *   - `wide`   - max-w-7xl with a max-w-3xl inner heading constraint (utility pages)
 *   - `narrow` - max-w-4xl with no heading constraint (Legal pages)
 *
 * Composable surface:
 *   - default slot replaces `title` prop for headings that need inline markup
 *   - `lead` slot replaces `lead` prop for paragraphs that need links / formatting
 *   - `actions` slot renders below the lead - for search inputs, stats strips,
 *     or any extra hero content (Gallery/Index uses this for stats + search)
 */
defineProps({
    eyebrow: {
        type: String,
        required: true,
    },
    title: {
        type: String,
        default: '',
    },
    lead: {
        type: String,
        default: '',
    },
    highlight: {
        type: String,
        default: '',
    },
    headingSize: {
        type: String,
        default: 'lg',
        validator: (v) => ['xs', 'sm', 'md', 'lg', 'xl', 'hero'].includes(v),
    },
    width: {
        type: String,
        default: 'wide',
        validator: (v) => ['narrow', 'wide'].includes(v),
    },
    tone: {
        type: String,
        default: 'standard',
        validator: (v) => ['standard', 'quiet'].includes(v),
    },
})
</script>

<template>
    <header class="bg-surface-dim relative overflow-hidden border-b border-zinc-900">
        <!-- Soft orange blur orb behind the content. Opacity + tint vary by
             tone so legal documents read serious, marketing-y pages read warm. -->
        <div
            class="pointer-events-none absolute inset-0"
            :class="tone === 'quiet' ? 'opacity-20' : 'opacity-30'"
        >
            <div
                class="absolute -top-32 left-1/4 h-125 w-125 rounded-full blur-[140px]"
                :class="tone === 'quiet' ? 'bg-orange-500/10' : 'bg-orange-500/20'"
            />
        </div>

        <div
            class="relative mx-auto px-6 md:px-12"
            :class="width === 'narrow' ? 'max-w-4xl py-16 md:py-20' : 'max-w-7xl py-20 md:py-28'"
        >
            <Heading
                :class="width === 'wide' ? 'max-w-3xl' : ''"
                as="h1"
                :size="headingSize"
                :eyebrow="eyebrow"
                :text="title"
                :highlight="highlight"
            >
                <slot v-if="!title" />
            </Heading>

            <p
                class="text-on-surface-variant mt-6 max-w-2xl text-base leading-relaxed md:text-lg"
                v-if="$slots.lead || lead"
            >
                <slot name="lead">{{ lead }}</slot>
            </p>

            <slot name="actions" />
        </div>
    </header>
</template>
